import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../axiosInstance';
import AuthContext from '../AuthContext';
import { LanguageContext } from '../LanguageContext';
import { resolveMessageComposeUnavailableReason } from './messageComposeAvailability';

const copy = {
  en: {
    titleNew: 'New message',
    titleReply: 'Reply',
    loading: 'Loading�',
    subjectLabel: 'Subject',
    messageLabel: 'Message',
    attachmentsLabel: 'Attachments (optional)',
    attachmentsHint: 'You can upload PDF, JPG, PNG, BMP or TIFF files (maximum 10MB each).',
    send: 'Send message',
    cancel: 'Cancel',
    sending: 'Sending�',
    loadError: 'We could not load the message. Please try again.',
    noCaseAccess: 'You can send a new message once your portal account is linked to your current case.',
    participantAccessConflict: 'We cannot confirm that your portal account matches the participant linked to this case. Contact program support.',
    noAssigneeAccess: 'You can send a new message once a program team member is assigned to your current case.',
    contextAccessUnavailable: 'New messaging is unavailable while we confirm your current case and assigned program contact.',
    modalTitle: 'Message sent',
    modalBody: 'Your message has been sent successfully.',
    modalClose: 'Return to inbox',
    backToDashboard: 'Back to dashboard'
  },
  fr: {
    titleNew: 'Nouveau message',
    titleReply: 'R�pondre',
    loading: 'Chargement�',
    subjectLabel: 'Objet',
    messageLabel: 'Message',
    attachmentsLabel: 'Pi�ces jointes (facultatif)',
    attachmentsHint: 'Formats accept�s : PDF, JPG, PNG, BMP ou TIFF (10 Mo maximum).',
    send: 'Envoyer',
    cancel: 'Annuler',
    sending: 'Envoi�',
    loadError: 'Impossible de charger le message. Veuillez r�essayer.',
    noCaseAccess: 'Vous pourrez envoyer un nouveau message lorsque votre compte du portail sera lié à votre dossier actuel.',
    participantAccessConflict: 'Nous ne pouvons pas confirmer que votre compte du portail correspond à la personne liée à ce dossier. Communiquez avec le soutien du programme.',
    noAssigneeAccess: 'Vous pourrez envoyer un nouveau message lorsqu’un membre de l’équipe du programme sera affecté à votre dossier actuel.',
    contextAccessUnavailable: 'La nouvelle messagerie est indisponible pendant que nous confirmons votre dossier actuel et la personne-ressource du programme.',
    modalTitle: 'Message envoy�',
    modalBody: 'Votre message a �t� envoy� avec succ�s.',
    modalClose: 'Retour � la bo�te de r�ception',
    backToDashboard: 'Retour au tableau de bord'
  }
};

const MODAL_STYLES = `
  .app-modal__backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  .app-modal {
    background: #fff;
    padding: 2rem;
    max-width: 24rem;
    border: 5px solid #0b0c0c;
    text-align: center;
  }
`;

function createMessageSendOperationId() {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `portal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

const ComposeMessage = () => {
  const { user } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const t = useMemo(() => copy[language] || copy.en, [language]);
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const replyToId = searchParams.get('replyTo');

  const [context, setContext] = useState({ loading: true, canCompose: false, applicationId: null });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState('');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const sendAttemptRef = useRef(null);
  const composeTargetVersionRef = useRef(0);
  const composeMode = replyToId ? 'reply' : 'new';
  const [replyTarget, setReplyTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    composeTargetVersionRef.current += 1;
    // A client-side route change selects a different immutable compose target.
    // Unknown-outcome retries may reuse a key only within that one target.
    sendAttemptRef.current = null;
    setReplyTarget(null);
    setContext({ loading: true, canCompose: false, applicationId: null });
    setSubject('');
    setBody('');
    setAttachments([]);
    setAttachmentError('');
    setUploadingAttachment(false);
    setIsSubmitting(false);
    setShowModal(false);
    setError('');
    const init = async () => {
      try {
        if (replyToId) {
          const { data } = await api.get(`/api/messages/${replyToId}`);
          if (cancelled) return;
          if (String(data?.id) !== String(replyToId)) {
            throw new Error('reply target response mismatch');
          }
          setReplyTarget(data);
          setContext({
            loading: false,
            canCompose: true,
            caseId: data.case_id || null,
            applicationId: data.application_id || null,
          });
          const replySubject = data.subject?.toLowerCase().startsWith('re:')
            ? data.subject
            : `Re: ${data.subject || ''}`;
          setSubject(replySubject);
          const quoted = data.body
            ? data.body.split('\n').map((line) => `> ${line}`).join('\n')
            : '';
          setBody(quoted ? `\n\n${quoted}` : '');
          return;
        }
        const ctxRes = await api.get('/api/messages/context');
        if (!cancelled) setContext({ loading: false, ...ctxRes.data });
      } catch (err) {
        console.error('Failed to initialise compose view', err);
        if (!cancelled) {
          setReplyTarget(null);
          setContext({ loading: false, canCompose: false, applicationId: null });
          setError(t.loadError);
        }
      }
    };
    if (user) init();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyToId, user]);

  const legacyDirectUpload = async (file, options = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (options.uploadId) form.append('uploadId', options.uploadId);
    const response = await api.post('/api/upload-application-file', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (!response || !response.data?.filePath) {
      throw new Error(language === 'fr' ? "Impossible d'enregistrer la pièce jointe." : 'Could not store attachment.');
    }
    return {
      uploadId: options.uploadId || null,
      filePath: response.data.filePath,
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream'
    };
  };

  const uploadAttachment = async (file) => {
    const payload = {
      filename: file.name,
      size: file.size,
      contentType: file.type || 'application/octet-stream'
    };
    let presign;
    try {
      presign = await api.post('/api/uploads/presign', payload).then(r => r.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        return legacyDirectUpload(file);
      }
      throw new Error(err?.response?.data?.error || (language === 'fr' ? 'Le téléversement a échoué.' : 'Upload failed.'));
    }
    if (presign.mode === 's3' && presign.presigned?.url) {
      try {
        await fetch(presign.presigned.url, {
          method: presign.presigned.method || 'PUT',
          headers: presign.presigned.headers || { 'Content-Type': file.type || 'application/octet-stream' },
          body: file
        });
      } catch (uploadErr) {
        const detail = uploadErr?.message || (language === 'fr' ? 'Impossible de joindre le stockage.' : 'Could not reach storage service.');
        throw new Error(detail);
      }
      return {
        uploadId: presign.uploadId,
        key: presign.key,
        name: file.name,
        size: file.size,
        type: file.type || 'application/octet-stream'
      };
    }
    // Fallback to legacy local endpoint (should be rare)
    return legacyDirectUpload(file, { uploadId: presign.uploadId });
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (event.target) {
      event.target.value = '';
    }
    if (!files.length) return;
    const targetVersion = composeTargetVersionRef.current;
    setAttachmentError('');
    setUploadingAttachment(true);
    try {
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await uploadAttachment(file);
        if (composeTargetVersionRef.current !== targetVersion) return;
        sendAttemptRef.current = null;
        setAttachments(prev => [...prev, uploaded]);
      }
    } catch (err) {
      console.error('Attachment upload failed', err);
      if (composeTargetVersionRef.current === targetVersion) {
        setAttachmentError(err?.message || t.loadError);
      }
    } finally {
      if (composeTargetVersionRef.current === targetVersion) {
        setUploadingAttachment(false);
      }
    }
  };

  const handleRemoveAttachment = (index) => {
    sendAttemptRef.current = null;
    setAttachments(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleCancel = () => {
    navigate('/messages');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    if (composeMode === 'reply' && !replyTarget) {
      setError(t.loadError);
      return;
    }
    if (!subject.trim() || !body.trim()) {
      setError(t.loadError);
      return;
    }
    if (uploadingAttachment) {
      setError(language === 'fr' ? 'Veuillez attendre la fin des téléversements.' : 'Please wait for uploads to finish.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    const targetVersion = composeTargetVersionRef.current;
    try {
      const payload = {
        compose_mode: composeMode,
        subject: subject.trim(),
        body: body.trim(),
        client_operation_id: sendAttemptRef.current || createMessageSendOperationId()
      };
      sendAttemptRef.current = payload.client_operation_id;
      if (composeMode === 'reply') {
        payload.reply_to = replyTarget.id;
      }
      if (composeMode === 'new' && context.applicationId) {
        payload.application_id = context.applicationId;
      }
      if (composeMode === 'new' && context.caseId) {
        payload.case_id = context.caseId;
      }
      if (attachments.length > 0) {
        payload.attachments = attachments.map(att => ({
          uploadId: att.uploadId,
          key: att.key,
          filePath: att.filePath,
          originalFilename: att.name
        }));
      }
      await api.post('/api/messages/reply-with-attachments', payload);
      if (composeTargetVersionRef.current !== targetVersion) return;
      sendAttemptRef.current = null;
      if (composeMode === 'reply' && replyTarget) {
        try {
          await api.put(`/api/messages/${replyTarget.id}/replied`);
        } catch (err) {
          console.warn('Failed to mark message as replied', err);
        }
      }
      if (composeTargetVersionRef.current !== targetVersion) return;
      setAttachments([]);
      setShowModal(true);
    } catch (err) {
      console.error('Failed to send message', err);
      if (composeTargetVersionRef.current !== targetVersion) return;
      if (Number(err?.response?.status) >= 400 && Number(err?.response?.status) < 500) {
        sendAttemptRef.current = null;
      }
      setError(t.loadError);
    } finally {
      if (composeTargetVersionRef.current === targetVersion) {
        setIsSubmitting(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    navigate('/messages', { state: { messageSent: true } });
  };

  const notAllowed = !context.loading && (
    (composeMode === 'new' && !context.canCompose) ||
    (composeMode === 'reply' && !replyTarget)
  );
  const composeUnavailableReason = resolveMessageComposeUnavailableReason(context);
  const composeUnavailableMessage = composeUnavailableReason === 'participant'
    ? t.participantAccessConflict
    : composeUnavailableReason === 'case'
      ? t.noCaseAccess
      : composeUnavailableReason === 'assignee'
        ? t.noAssigneeAccess
        : t.contextAccessUnavailable;

  return (
    <div className="govuk-width-container govuk-!-margin-top-3">
      <style>{MODAL_STYLES}</style>
      <main className="govuk-main-wrapper" role="main">
        <h1 className="govuk-heading-l">{composeMode === 'new' ? t.titleNew : t.titleReply}</h1>
        {error && (
          <div className="govuk-error-summary" role="alert" aria-live="assertive">
            <h2 className="govuk-error-summary__title">{error}</h2>
          </div>
        )}
        {context.loading ? (
          <p className="govuk-body">{t.loading}</p>
        ) : notAllowed ? (
          <div className="govuk-body">
            <p>{composeMode === 'reply' ? t.loadError : composeUnavailableMessage}</p>
            <p>
              <Link to="/dashboard" className="govuk-link">
                &lt; {t.backToDashboard || 'Back to dashboard'}
              </Link>
            </p>
          </div>
        ) : (
          <form className="govuk-!-margin-top-4" onSubmit={handleSubmit}>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="compose-subject">{t.subjectLabel}</label>
              <input
                id="compose-subject"
                className="govuk-input"
                type="text"
                value={subject}
                onChange={(event) => {
                  sendAttemptRef.current = null;
                  setSubject(event.target.value);
                }}
              />
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="compose-body">{t.messageLabel}</label>
              <textarea
                id="compose-body"
                className="govuk-textarea"
                rows={10}
                value={body}
                onChange={(event) => {
                  sendAttemptRef.current = null;
                  setBody(event.target.value);
                }}
              />
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="compose-attachments">{t.attachmentsLabel}</label>
              <span className="govuk-hint">{t.attachmentsHint}</span>
              <input
                id="compose-attachments"
                className="govuk-file-upload"
                type="file"
                multiple
                accept="application/pdf,image/jpeg,image/png,image/bmp,image/tiff"
                onChange={handleAttachmentChange}
                disabled={uploadingAttachment}
              />
              {attachmentError && (
                <p className="govuk-error-message govuk-!-margin-top-2">{attachmentError}</p>
              )}
              {attachments.length > 0 && (
                <ul className="govuk-list govuk-list--bullet govuk-!-margin-top-2">
                  {attachments.map((file, index) => (
                    <li key={`${file.name}-${index}`}>
                      {file.name}{' '}
                      <button
                        type="button"
                        className="govuk-link"
                        onClick={() => handleRemoveAttachment(index)}
                        style={{ border: 'none', background: 'none', padding: 0 }}
                      >
                        {language === 'fr' ? 'Supprimer' : 'Remove'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {uploadingAttachment && (
                <p className="govuk-hint">{language === 'fr' ? 'Téléversement en cours...' : 'Uploading...'}</p>
              )}
            </div>
            <div className="govuk-button-group govuk-!-margin-top-4">
              <button type="submit" className="govuk-button" disabled={isSubmitting || uploadingAttachment}>
                {isSubmitting ? t.sending : t.send}
              </button>
              <button type="button" className="govuk-button govuk-button--secondary" onClick={handleCancel} disabled={isSubmitting}>
                {t.cancel}
              </button>
            </div>
          </form>
        )}
      </main>
      {showModal && (
        <div className="app-modal__backdrop" role="dialog" aria-modal="true">
          <div className="app-modal">
            <h2 className="govuk-heading-m">{t.modalTitle}</h2>
            <p className="govuk-body">{t.modalBody}</p>
            <button type="button" className="govuk-button" onClick={handleCloseModal}>{t.modalClose}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComposeMessage;
